/**
 * The Self Development section's own vocabulary, mapped onto the panel-wide
 * design tokens in component/common/designSystem/theme.js — the same split
 * visionBoardTheme.js, moodTheme.js and homeTestingTheme.js use: only domain
 * meaning lives here, palettes belong to the design system.
 */
import { CHART_COLORS } from '../../../../component/common/designSystem';

/** The six tools, in the order the section presents them everywhere. */
export const TOOL_ORDER = [
  'gratitude',
  'cleanse',
  'journal',
  'goals',
  'vision_board',
  'affirmations'
];

export const TOOL_LABELS = {
  gratitude: 'Gratitude',
  cleanse: 'Cleanse',
  journal: 'Journal',
  goals: 'Goals',
  vision_board: 'Vision Board',
  affirmations: 'Affirmations'
};

/**
 * One hue per tool, fixed. A tool keeps its colour on the overview donut, in
 * the tool-by-tool table and on its own page, so somebody scanning the section
 * learns the mapping once. No hue carries a verdict here — six tools are six
 * different things to do, not a scale.
 */
export const TOOL_COLORS = {
  gratitude: CHART_COLORS.amber,
  cleanse: CHART_COLORS.teal,
  journal: CHART_COLORS.blue,
  goals: CHART_COLORS.green,
  vision_board: CHART_COLORS.brand,
  affirmations: CHART_COLORS.pink
};

export const toolColor = (key) => TOOL_COLORS[key] || CHART_COLORS.gray;

/** Donut colours in TOOL_ORDER, for any chart whose slices are the six tools. */
export const TOOL_RAMP = TOOL_ORDER.map((key) => TOOL_COLORS[key]);

/**
 * Distinct days somebody practised, one day → many. Sequential rather than a
 * traffic light: showing up once is not a failure, it is the start of the only
 * curve this section is trying to move.
 */
export const CONSISTENCY_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.brandSoft,
  CHART_COLORS.blue,
  CHART_COLORS.teal,
  CHART_COLORS.green
];

/** How much one person writes — sparse to prolific. */
export const VOLUME_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.brandSoft,
  CHART_COLORS.brand,
  CHART_COLORS.blue,
  CHART_COLORS.moss
];

/**
 * How many of the six tools one person keeps going. Deliberately climbs to
 * green: breadth is the one thing on the overview that genuinely reads as
 * better, because a person using four tools has a practice and a person using
 * one has tried the app.
 */
export const BREADTH_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.brandSoft,
  CHART_COLORS.blue,
  CHART_COLORS.teal,
  CHART_COLORS.green
];

/**
 * Draft → in progress → done. Shared by Goals and Cleanse, which both run the
 * same three-state shape, so the two pages read identically.
 */
export const STATUS_RAMP = [CHART_COLORS.gray, CHART_COLORS.blue, CHART_COLORS.green];

/** A funnel through the steps of an exercise — full colour at the last step. */
export const FUNNEL_RAMP = [
  CHART_COLORS.brandSoft,
  CHART_COLORS.blue,
  CHART_COLORS.teal,
  CHART_COLORS.green
];

/** How long an achieved goal took. Neutral: fast is not better than considered. */
export const DURATION_RAMP = [
  CHART_COLORS.teal,
  CHART_COLORS.blue,
  CHART_COLORS.brand,
  CHART_COLORS.moss
];

/** Written their own versus played Shoorah's — two habits, two hues. */
export const SOURCE_RAMP = [CHART_COLORS.pink, CHART_COLORS.brand];
